import { Lightning, Router } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import TextLabel from 'components/common/TextLabel';
import { navigateHome } from 'support/routerUtils';
import { getFontFaceFromStyle } from 'support/textUtils';

export interface EmptyListSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  icon: Lightning.Element.PatchTemplate;
  title: string;
  description: string;
  buttonLabel: string;
  action: string;

  Icon: Lightning.Element.PatchTemplate;
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  Button: typeof Button;
}

const CONTENT_WIDTH = 1685;
const CONTENT_HEIGHT = 375;
const MARGIN_SPACE = 10;
const FONT_SIZE_TITLE = 34;
const FONT_SIZE_DESCRIPTION = 24;
const FONT_SIZE_DESCRIPTION_LINE_HEIGHT = 29;
const FONT_SIZE_BUTTON = 23;
const BUTTON_HEIGHT = 60;
const BUTTON_MIN_WIDTH = 255;

export default class EmptyListSection
  extends Lightning.Component<EmptyListSectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<EmptyListSectionTemplateSpec>
{
  private _icon: EmptyListSectionTemplateSpec['icon'] = {};
  private _title = '';
  private _description = '';
  private _buttonLabel = '';
  private _action = '';

  private _Icon = this.getByRef('Icon')!;
  private _Title = this.getByRef('Title')!;
  private _Description = this.getByRef('Description')!;
  private _Button = this.getByRef('Button')!;

  get announce() {
    return [this._Title.text?.text, this._Description.text?.text];
  }

  set title(title: string) {
    this._title = title;
  }

  get title() {
    return this._title;
  }

  set icon(icon: EmptyListSectionTemplateSpec['Icon']) {
    this._icon = icon;
  }

  get icon() {
    return this._icon;
  }

  set description(description: string) {
    this._description = description;
  }

  get description() {
    return this._description;
  }

  set buttonLabel(buttonLabel: string) {
    this._buttonLabel = buttonLabel;
  }

  get buttonLabel() {
    return this._buttonLabel;
  }

  set action(action: string) {
    this._action = action;
  }

  get action() {
    return this._action;
  }

  get button() {
    return this._Button;
  }

  static override _template(): Lightning.Component.Template<EmptyListSectionTemplateSpec> {
    return {
      w: CONTENT_WIDTH,
      h: CONTENT_HEIGHT,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Icon: {
        flexItem: { marginBottom: MARGIN_SPACE },
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: MARGIN_SPACE },
        text: {
          fontSize: FONT_SIZE_TITLE,
          fontFace: getFontFaceFromStyle('bold'),
        },
      },
      Description: {
        type: TextLabel,
        flexItem: { marginBottom: MARGIN_SPACE },
        text: {
          fontSize: FONT_SIZE_DESCRIPTION,
          fontFace: getFontFaceFromStyle('regular'),
          lineHeight: FONT_SIZE_DESCRIPTION_LINE_HEIGHT,
          textAlign: 'center',
        },
      },
      Button: {
        type: Button,
        height: BUTTON_HEIGHT,
        minWidth: BUTTON_MIN_WIDTH,
        fontSize: FONT_SIZE_BUTTON,
        fontFace: getFontFaceFromStyle('bold'),
        action: '$action',
        signals: {
          $action: true,
        },
        passSignals: { $onHover: true },
      },
    };
  }

  override _active() {
    this._Icon.patch({
      ...this._icon,
    });
    this._Title.patch({
      text: {
        text: this._title,
      },
    });

    this._Description.patch({
      text: {
        text: this._description,
      },
    });

    this._Button.patch({
      label: this._buttonLabel,
    });
  }

  override _getFocused() {
    return this._Button;
  }

  private $action() {
    this._action ? Router.navigate(this._action) : navigateHome();
  }
}
